import { z } from 'zod';
import {
  AdminAuditListResponse,
  AdminDashboardResponse,
  AdminTenantDetail,
  AdminTenantListResponse,
  type ActivateRequest,
  type AdminActionResult,
  type AdminAuditEntry,
  type AdminTenantListQuery,
  type AdminTenantSummary,
  type BlockRequest,
  type ExtendTrialRequest,
  type ManualPaymentRequest,
  type RestrictRequest,
} from '@odonix/contracts';

/**
 * Единственная дверь клиента к `/v1/tenants` и `/v1/audit` платформенной
 * админки. Прямого `fetch` в экранах нет, тем же правилом, что и в
 * `apps/webapp`.
 */

type ApiFetch = (path: string, init?: RequestInit) => Promise<Response>;

export class TenantsError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly fields?: Record<string, string>,
  ) {
    super(message);
    this.name = 'TenantsError';
  }
}

const ErrorBody = z.object({
  code: z.string(),
  message: z.string(),
  fields: z.record(z.string(), z.string()).optional(),
});

async function toError(response: Response): Promise<TenantsError> {
  const parsed = ErrorBody.safeParse(await response.json().catch(() => null));
  return parsed.success
    ? new TenantsError(parsed.data.code, parsed.data.message, parsed.data.fields)
    : new TenantsError('UNKNOWN', 'Сервер не ответил. Проверьте связь и повторите');
}

export interface TenantsApi {
  dashboard(): Promise<AdminDashboardResponse>;
  list(query: Partial<AdminTenantListQuery>): Promise<{ tenants: AdminTenantSummary[]; nextCursor: string | null }>;
  detail(tenantId: string): Promise<AdminTenantDetail>;
  extendTrial(tenantId: string, body: ExtendTrialRequest): Promise<AdminActionResult>;
  activate(tenantId: string, body: ActivateRequest): Promise<AdminActionResult>;
  manualPayment(tenantId: string, body: ManualPaymentRequest): Promise<AdminActionResult>;
  block(tenantId: string, body: BlockRequest): Promise<AdminActionResult>;
  restrict(tenantId: string, body: RestrictRequest): Promise<AdminActionResult>;
  /** Закрепить клинику за менеджером; `null` — снять менеджера. */
  assignManager(tenantId: string, managerId: string | null): Promise<void>;
  audit(query: {
    tenantId?: string | undefined;
    cursor?: string | undefined;
    limit?: number | undefined;
  }): Promise<{
    entries: AdminAuditEntry[];
    nextCursor: string | null;
  }>;
}

export function createTenantsApi(apiFetch: ApiFetch): TenantsApi {
  async function get(path: string): Promise<unknown> {
    const response = await apiFetch(path);
    if (!response.ok) throw await toError(response);
    return response.json();
  }

  async function post(path: string, body: unknown): Promise<unknown> {
    const response = await apiFetch(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw await toError(response);
    return response.json();
  }

  return {
    async dashboard() {
      return AdminDashboardResponse.parse(await get('/v1/dashboard'));
    },

    async list(query) {
      const params = new URLSearchParams();
      if (query.q !== undefined && query.q !== '') params.set('q', query.q);
      if (query.state !== undefined) params.set('state', query.state);
      if (query.kind !== undefined) params.set('kind', query.kind);
      if (query.managerId !== undefined) params.set('managerId', query.managerId);
      if (query.cursor !== undefined) params.set('cursor', query.cursor);
      if (query.limit !== undefined) params.set('limit', String(query.limit));

      const parsed = AdminTenantListResponse.parse(await get(`/v1/tenants?${params.toString()}`));
      return parsed;
    },

    async detail(tenantId) {
      return AdminTenantDetail.parse(await get(`/v1/tenants/${encodeURIComponent(tenantId)}`));
    },

    async extendTrial(tenantId, body) {
      return post(`/v1/tenants/${encodeURIComponent(tenantId)}/extend-trial`, body) as Promise<AdminActionResult>;
    },

    async activate(tenantId, body) {
      return post(`/v1/tenants/${encodeURIComponent(tenantId)}/activate`, body) as Promise<AdminActionResult>;
    },

    async manualPayment(tenantId, body) {
      return post(`/v1/tenants/${encodeURIComponent(tenantId)}/manual-payment`, body) as Promise<AdminActionResult>;
    },

    async block(tenantId, body) {
      return post(`/v1/tenants/${encodeURIComponent(tenantId)}/block`, body) as Promise<AdminActionResult>;
    },

    async restrict(tenantId, body) {
      return post(`/v1/tenants/${encodeURIComponent(tenantId)}/restrict`, body) as Promise<AdminActionResult>;
    },

    async assignManager(tenantId, managerId) {
      const response = await apiFetch(`/v1/tenants/${encodeURIComponent(tenantId)}/manager`, {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ managerId }),
      });
      if (!response.ok) throw await toError(response);
    },

    async audit(query) {
      const params = new URLSearchParams();
      if (query.tenantId !== undefined) params.set('tenantId', query.tenantId);
      if (query.cursor !== undefined) params.set('cursor', query.cursor);
      if (query.limit !== undefined) params.set('limit', String(query.limit));

      return AdminAuditListResponse.parse(await get(`/v1/audit?${params.toString()}`));
    },
  };
}
